import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import { useIsAdmin } from 'hooks/usePermissions';
import { useAuthReadiness } from 'hooks/useAuthReadiness';
import defaultLogo from 'assets/icons/logo.png';
import CgPageLoader from 'modules/Common/layout/CgPageLoader';
import RouteAccessGate from 'modules/Common/permissions/RouteAccessGate';
import { usePermissionsContext } from 'modules/Common/permissions/PermissionsProvider';
import { useTranslation } from 'react-i18next';
import { useBranding } from '../hooks/useBranding';
import { overviewPathForOrgType } from '../overviewPath';
import PermissionRoute from './PermissionRoute';

// Layered after PermissionRoute. When `required` is true, only backend-computed
// org admins pass; everyone else sees "Access Denied" (same UX as a permission
// denial). Used for the Roles & Permissions admin UI.
//
// Like PermissionRoute, this defers while /permissions/me is in flight —
// otherwise the post-org-switch window (during which `me` is null and
// `useIsAdmin` returns false) would deny an admin. And like every guard here,
// the denial RENDERS rather than navigating, so a denial taken on a payload
// that had not settled yet disappears by itself once the real one lands.
// See docs/features/denial-never-navigates/00-OVERVIEW.md (N1-N3).
export const AdminGuard = ({ required, children }) => {
  if (!required) return children;
  return <RequiredAdminGuard>{children}</RequiredAdminGuard>;
};

export const EntitlementGuard = ({ required, children }) => {
  if (!required) return children;
  return <RequiredEntitlementGuard entitlement={required}>{children}</RequiredEntitlementGuard>;
};

const RequiredEntitlementGuard = ({ entitlement, children }) => {
  const { status, hasEntitlement } = usePermissionsContext();

  // No `requiredPermission`: an entitlement is not a permission slug, and
  // showing the user `SupplierEntitlement.Journey` as though it were one would
  // send them looking for a permission that does not exist.
  return (
    <RouteAccessGate
      status={status}
      allowed={hasEntitlement(entitlement)}
      pending={<RouteReadinessLoader />}
    >
      {children}
    </RouteAccessGate>
  );
};

const RequiredAdminGuard = ({ children }) => {
  const { status } = usePermissionsContext();
  const isAdmin = useIsAdmin();

  return (
    <RouteAccessGate
      status={status}
      allowed={isAdmin}
    >
      {children}
    </RouteAccessGate>
  );
};

// Resolve a per-page resourceKey, optionally per-area. Strings apply to any
// org type; an object maps org type → resourceKey for routes shared between
// the declarant and operator areas (e.g. /cost-radar, /carbon-price-tracker,
// /upload-hub/*).
export const resolveResourceKey = (resourceKey, userType) => {
  if (!resourceKey) return null;
  if (typeof resourceKey === 'string') return resourceKey;
  return resourceKey[userType] || null;
};

export const RouteReadinessLoader = () => {
  const { t } = useTranslation('app');
  const brand = useBranding();
  const logo = brand.logoUrl || defaultLogo;

  return (
    <CgPageLoader
      variant="fullscreen"
      logo={logo}
      brandName={brand.name}
      primaryColor={brand.primaryColor}
      ariaLabel={t('routeLoader.ariaLabel', 'Loading your workspace')}
    />
  );
};

// Custom PrivateRoute component
// routeTypes is an array of possible userTypes who can use this route
// currently each page in the system is only accessible by a consultant, operator, or declarant separatly
// as such there is no need to do it this way but I feel this is more extensible in the future to avoid
// editing the private routes component
//
// Optional `resourceKey` adds a User Matrix permission check on top of the
// org-type gate: the user must have `org:{resourceKey}:read` (see
// [permissionsMatrix.js](src/modules/Common/config/permissionsMatrix.js)).
// Pass an object `{ [OrganizationType.X]: 'key', ... }` for routes shared
// between areas.
export const PrivateRoute = ({
  component: Component,
  render: renderFn,
  allowedTypes,
  resourceKey,
  requiredEntitlement,
  adminOnly = false,
  exact = true,
  ...children
}) => {
  const { status, organization } = useAuthReadiness();
  const userType = organization?.publicMetadata?.type;

  return (
    <Route
      {...children}
      exact={exact}
      render={(props) => {
        if (status === 'loading') return <RouteReadinessLoader />;
        // Not a denial: the user has no workspace at all, and /init is where
        // one gets chosen. This redirect stays a redirect.
        if (status === 'no-org') return <Redirect to="/init" />;

        const allowed = allowedTypes.includes(userType);
        const home = overviewPathForOrgType(userType);
        if (!allowed && home) return <Redirect to={home} />;

        const content = renderFn ? renderFn(props) : <Component {...props} />;
        // `useAuthReadiness` reports 'ready' whenever `organization` is
        // non-null — it never inspects publicMetadata — so `userType` can
        // still be undefined here and `allowedTypes.includes(undefined)` is
        // false. That used to be a permanent /not-found; now it renders in
        // place and corrects itself the moment the type lands.
        return (
          <RouteAccessGate
            status="ready"
            allowed={allowed}
          >
            <PermissionRoute resourceKey={resolveResourceKey(resourceKey, userType)}>
              <EntitlementGuard required={requiredEntitlement}>
                <AdminGuard required={adminOnly}>{content}</AdminGuard>
              </EntitlementGuard>
            </PermissionRoute>
          </RouteAccessGate>
        );
      }}
    />
  );
};

export default PrivateRoute;
